import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import AuthStore from 'src/stores/AuthStore';
import UserStore from 'src/stores/UserStore';
import Button from 'src/components/Button';
import Card from 'src/components/Card';
import Flex from 'src/components/Flex';
import LoadingIcon from 'src/components/LoadingIcon';
import MfaSetupFlow from 'src/components/MfaSetupFlow';
import { MfaMethodType, MfaStatus } from 'src/api/mfa';
import { useCreateTrustedDeviceSession, useMfaStatus } from 'src/queries/useMfa';
import { RequestOrganization, getRequestOrgScope } from 'src/api/utils';
import { ROUTES } from 'src/utils/constants';
import { subscribeMfaTokenCleared } from 'src/utils/mfaToken';
import { MFA_GRACE_PERIOD_TIME_ZONE_LABEL } from 'common/lib/services/index/mfa/MfaGracePeriod';
import styles from './styles.module.scss';

const SUPPORT_EMAIL = 'support@graphiumhealth.com';

function formatDateOnly(date?: string | null) {
    if (!date) {
        return null;
    }
    const [year, month, day] = date.split('-').map((part) => parseInt(part, 10));
    if (!year || !month || !day) {
        return date;
    }
    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, day)));
}

function getSetupMethodTypes(status: MfaStatus): MfaMethodType[] {
    return (status.policy.allowedMethods || []).filter(
        (method, index, methods) =>
            methods.indexOf(method) === index &&
            (method === 'totp' || method === 'sms' || (method === 'email' && status.user.accountEmail?.verifiedInd)),
    );
}

function MfaGate({
    children,
    organization,
    onLogout,
}: {
    children: React.ReactNode;
    organization?: RequestOrganization;
    onLogout?: () => void;
}) {
    const authStore = useContext(AuthStore);
    const userStore = useContext(UserStore);
    const orgScope = getRequestOrgScope(organization);
    const statusQueryEnabled =
        authStore.isAuthenticated &&
        (!authStore.isLoadingStartupData || Boolean(organization)) &&
        !authStore.hasNoActiveOrgs &&
        Boolean(userStore.usrNm) &&
        Boolean(orgScope);
    const statusQuery = useMfaStatus(statusQueryEnabled, organization);
    const trustedDeviceSession = useCreateTrustedDeviceSession(organization);
    const [locallySatisfiedOrgScope, setLocallySatisfiedOrgScope] = useState<string | number | null>(null);
    const [trustedDeviceAttemptedOrgScope, setTrustedDeviceAttemptedOrgScope] = useState<string | number | null>(null);
    const status = statusQuery.data;
    const localSatisfied = locallySatisfiedOrgScope === orgScope;
    const trustedDeviceAttempted = trustedDeviceAttemptedOrgScope === orgScope;

    useEffect(() => {
        return subscribeMfaTokenCleared((clearedOrgScope) => {
            const currentOrgScope = orgScope === undefined || orgScope === null ? null : `${orgScope}`;
            if (clearedOrgScope && clearedOrgScope !== currentOrgScope) {
                return;
            }

            setLocallySatisfiedOrgScope(null);
            setTrustedDeviceAttemptedOrgScope(null);
            if (statusQueryEnabled) {
                statusQuery.refetch();
            }
        });
    }, [orgScope, statusQuery, statusQueryEnabled]);

    useEffect(() => {
        if (status && !status.satisfied && localSatisfied) {
            setLocallySatisfiedOrgScope(null);
        }
    }, [localSatisfied, status]);

    useEffect(() => {
        const shouldTryTrustedDevice =
            statusQueryEnabled &&
            status &&
            !localSatisfied &&
            !trustedDeviceAttempted &&
            status.policy.enforced &&
            status.policy.trustedDeviceEnabled &&
            !status.satisfied;

        if (!shouldTryTrustedDevice) {
            return;
        }

        setTrustedDeviceAttemptedOrgScope(orgScope || null);
        trustedDeviceSession.mutate(undefined, {
            onSuccess: () => {
                setLocallySatisfiedOrgScope(orgScope || null);
                if (!organization) authStore.setLoadingStartupData('login');
            },
        });
    }, [authStore, localSatisfied, orgScope, status, statusQueryEnabled, trustedDeviceAttempted, trustedDeviceSession]);

    if (!statusQueryEnabled) {
        return <>{children}</>;
    }

    if (statusQuery.isLoading || trustedDeviceSession.isLoading) {
        return (
            <div className={styles.page}>
                <Flex justify="center" align="center" className={styles.loading}>
                    <LoadingIcon />
                </Flex>
            </div>
        );
    }

    if (statusQuery.error || !status) {
        return (
            <div className={styles.page}>
                <Card className={styles.card} label="Multi-factor authentication">
                    <div className={styles.body}>
                        <div className={styles.error}>Unable to load MFA status.</div>
                        <div className={styles.footer}>
                            <Button type="secondary" text="Retry" onClick={() => statusQuery.refetch()} />
                            <Button type="transparent" text="Sign out" onClick={onLogout || authStore.logout} />
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    const shouldShowGraceBanner =
        status.policy.enabled &&
        status.policy.required &&
        status.policy.inGracePeriod &&
        status.setupRequired &&
        !status.user.exempt &&
        !localSatisfied;
    const shouldGate =
        status.policy.enabled &&
        !status.user.exempt &&
        !localSatisfied &&
        !status.policy.inGracePeriod &&
        !status.satisfied &&
        (status.setupRequired || status.policy.required);
    const setupRequired = shouldGate && status.setupRequired;
    const setupMethodTypes = setupRequired ? getSetupMethodTypes(status) : [];

    if (!shouldGate) {
        return (
            <>
                {shouldShowGraceBanner ? (
                    <div className={styles.graceBanner}>
                        <span>
                            Multi-factor authentication will be required
                            {status.policy.gracePeriodEndDate ? (
                                <>
                                    {' after '}
                                    {formatDateOnly(status.policy.gracePeriodEndDate)}
                                    {` (${MFA_GRACE_PERIOD_TIME_ZONE_LABEL})`}
                                </>
                            ) : null}
                            .
                        </span>
                        <Link className={styles.graceLink} to={ROUTES.getString(ROUTES.Profile)}>
                            Manage MFA
                        </Link>
                    </div>
                ) : null}
                {children}
            </>
        );
    }

    function completeMfaVerification() {
        setLocallySatisfiedOrgScope(orgScope || null);
        if (!organization) authStore.setLoadingStartupData('login');
    }

    return (
        <div className={styles.page}>
            <Card className={styles.card} label="Multi-factor authentication">
                <div className={styles.body}>
                    <div className={styles.title}>
                        {setupRequired ? 'Set up multi-factor authentication' : 'Verify your account'}
                    </div>
                    <div className={styles.description}>
                        {setupRequired
                            ? 'Multi-factor authentication is required for this organization before you can continue.'
                            : 'Complete verification with one of your enrolled methods before continuing.'}
                    </div>

                    {setupRequired && setupMethodTypes.length === 0 ? (
                        <div className={styles.error}>
                            No MFA setup method is available for this organization. Contact support to regain access.
                        </div>
                    ) : (
                        <MfaSetupFlow
                            organization={organization}
                            status={status}
                            methodTypes={setupRequired ? setupMethodTypes : undefined}
                            purpose={setupRequired ? 'enroll' : 'verify'}
                            allowRememberDevice={status.policy.enforced && status.policy.trustedDeviceEnabled}
                            onVerified={completeMfaVerification}
                        />
                    )}

                    <div className={styles.footer}>
                        <a className={styles.supportLink} href={`mailto:${SUPPORT_EMAIL}`}>
                            Contact support
                        </a>
                        <Button type="transparent" text="Sign out" onClick={onLogout || authStore.logout} />
                    </div>
                </div>
            </Card>
        </div>
    );
}

export default observer(MfaGate);
